import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getExpense } from '../../api/operatingCosts'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import ExpenseFiles from './ExpenseFiles'
import { KIND_LABEL } from './expensesShared'

// One expense: the entry it posted, what's still owed if it was on account,
// and the bill or receipts attached to it.
export default function ExpenseDetail() {
  const { jeId } = useParams()
  const { roles, has } = useSession()
  const books = has('accounting')  // without it: category and how it was paid, no debits or credits
  const seesBooks = (roles.includes('admin') || roles.includes('accounting')) && books
  const payPage = (roles.includes('admin') || roles.includes('purchase')) && has('purchase') ? '/purchase/ap-ledger' : ((roles.includes('admin') || roles.includes('accounting')) && has('accounting') ? '/accounting/ap/payment' : '')
  const [exp, setExp] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    getExpense(jeId)
      .then(setExp)
      .catch((e) => setError(e.message || 'Could not load the expense.'))
      .finally(() => setLoading(false))
  }, [jeId])

  const total = (exp?.debits || []).reduce((s, d) => s + Number(d.amount || 0), 0)
  const bill = exp?.bill

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/operating/expenses" label="Expenses" />}
        title={exp ? (exp.what || 'Expense') : 'Expense'}
        badge={exp && <StatusBadge label={KIND_LABEL[exp.kind] || 'Expense'} tone={exp.kind === 'entered' ? 'info' : 'muted'} />}
        meta={exp && (
          <>
            {fmtDay(exp.date)}
            {exp.vendor_name && ` · ${exp.vendor_name}`}
            {seesBooks && <> · <Link to={`/accounting/journal/${exp.je_id}`}>Journal entry {docNo('je', exp.je_no)}</Link></>}
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading expense…" /> : exp && (
        <>
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>{books ? 'Entry' : 'Details'}</h2>
              <span>{money(total)}</span>
            </div>
            {!books ? (
              <table className="ci-line-table exp-entry-table">
                <tbody>
                  <tr><td>Paid with <span className="cell-sub">{bill ? 'Not paid yet (owed to the vendor)' : exp.credits.map((c) => c.name).join(', ')}</span></td><td className="num" /></tr>
                </tbody>
              </table>
            ) : (
            <div className="doc-table-scroll">
              <table className="ci-line-table exp-entry-table">
                <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
                <tbody>
                  {exp.debits.map((d) => (
                    <tr key={`d${d.code}`}><td>{d.code} · {d.name}</td><td className="num">{money(d.amount)}</td><td className="num">–</td></tr>
                  ))}
                  {exp.credits.map((c) => (
                    <tr key={`c${c.code}`}><td>{c.code} · {c.name}</td><td className="num">–</td><td className="num">{money(c.amount)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {bill && (
              <p className="doc-hint exp-owed">
                {bill.owed > 0
                  ? <>On account: {money(bill.owed)} still owed to {exp.vendor_name || 'the vendor'} on bill {bill.number || docNo('bill', bill.bill_no)}{bill.due_date ? `, due ${fmtDay(bill.due_date)}` : ''}.</>
                  : <>On account: paid to {exp.vendor_name || 'the vendor'} in full.</>}
                {bill.owed > 0 && payPage && <> <Link to={`${payPage}?vendor_id=${exp.vendor_id}`}>Pay it</Link></>}
              </p>
            )}
          </section>

          <ExpenseFiles jeId={exp.je_id} files={exp.files} onChange={(files) => setExp((x) => ({ ...x, files }))} onError={setError} />
        </>
      )}
    </div>
  )
}
